import { FC, ReactNode, useCallback, useLayoutEffect, useState } from 'react';
import { NavigationItemResponse } from './types';
import styles from './Navigation.module.css';
import NavigationList from './NavigationList';
import { Icon } from '@kpa/kul';

export interface NavigationItemProps {
  item: NavigationItemResponse;
  callback?: (item: NavigationItemResponse) => void;
  currentItem?: NavigationItemResponse;
}

const NavigationItem: FC<NavigationItemProps> = ({
  item,
  callback,
  currentItem,
}): ReactNode => {
  const [expanded, setExpanded] = useState<boolean>(false);

  const isCurrentItem = useCallback(() => {
    if (!currentItem)
      return false;
    return currentItem.id === item.id;
  }, [item, currentItem]);

  const hasCurrentAmongChildren = useCallback(() => {
    if (!item.children) return false;
    if (!currentItem) return false;
    return item.children.some((child) => {
      return child.id === currentItem.id;
    });
  }, [item, currentItem]);

  useLayoutEffect(() => {
    setExpanded(hasCurrentAmongChildren());
  }, [hasCurrentAmongChildren]);

  const hasChildren = (item: NavigationItemResponse): boolean => {
    return item.children?.length > 0;
  };

  const controlledId = `submenufor_${item.id}`;

  const handleClick = (e: React.MouseEvent) => {
    if (callback) {
      e.preventDefault();
      callback(item);
    }
  };

  return (
    <>
      <li className={styles.navitem} aria-current={isCurrentItem()}>
        <div
          className={`${styles.navitemlabelcontainer} ${isCurrentItem() ? styles.active : ''} `}
        >
          <a
            href={item.url}
            className={styles.navitemlabel}
            onClick={handleClick}
          >
            {item.name}
          </a>
          {hasChildren(item) && (
            <button
              className={styles.navitemcaret}
              aria-label={`${expanded ? 'Minimera' : 'Visa'} länkar till sidor under ${item.name}`}
              aria-expanded={expanded}
              aria-controls={controlledId}
              onClick={() => setExpanded(!expanded)}
            >
              <Icon
                name={expanded ? 'IconCaretUp' : 'IconCaretDown'}
                color="backgroundPrimary"
                size="medium"
              />
            </button>
          )}
        </div>
        {hasChildren(item) && (
          <NavigationList
            items={item.children}
            expanded={expanded}
            id={controlledId}
            currentItem={currentItem}
            callback={callback}
          />
        )}
      </li>
    </>
  );
};

export default NavigationItem;
